// Browser-compatible structured logging utility
// Provides the same interface as our Winston implementation but works in browser environments

// Define log levels with numeric priorities (lower number = higher priority)
const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;

// Define colors for different log levels (for console styling)
const LOG_COLORS = {
  error: '#DC2626', // red-600
  warn: '#D97706', // amber-600
  info: '#059669', // emerald-600
  debug: '#2563EB', // blue-600
} as const;

// Determine log level based on environment
const getLogLevel = (): LogLevel => {
  if (typeof window !== 'undefined') {
    // Browser environment
    return import.meta.env.DEV ? 'debug' : 'warn';
  }
  // Node.js environment (for SSR/tests)
  return process?.env?.NODE_ENV === 'development' ? 'debug' : 'warn';
};

const currentLogLevel = getLogLevel();
const currentLogLevelPriority = LOG_LEVELS[currentLogLevel];

// Check if a log level should be output
const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVELS[level] <= currentLogLevelPriority;
};

// Format timestamp for consistent logging in Singapore timezone
const formatTimestamp = (): string => {
  // Use SingaporeTime for consistent timezone handling
  try {
    // Dynamic import to avoid circular dependencies and ensure browser compatibility
    // eslint-disable-next-line no-restricted-syntax, no-restricted-globals -- Intentional: Avoid circular dependency with SingaporeTime
    return new Date()
      .toLocaleString('en-CA', {
        timeZone: 'Asia/Singapore',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
      .replace(', ', ' ');
  } catch {
    // Fallback to simple timestamp if SingaporeTime is not available
    // eslint-disable-next-line no-restricted-syntax, no-restricted-globals -- Intentional: Fallback for logger
    return new Date().toLocaleString();
  }
};

// Enhanced console logging methods with styling
const logToConsole = (level: LogLevel, message: string, meta: LogMeta = {}) => {
  if (!shouldLog(level)) return;

  const timestamp = formatTimestamp();
  const color = LOG_COLORS[level];

  // Build log prefix
  let prefix = `${timestamp} [${level.toUpperCase()}]`;

  if (meta.service) {
    prefix += ` [${meta.service}]`;
  }

  if (meta.operation) {
    prefix += ` [${meta.operation}]`;
  }

  // Development environment: use styled console logs
  if (import.meta.env.DEV) {
    const style = `color: ${color}; font-weight: bold;`;

    console.groupCollapsed(`%c${prefix}: ${message}`, style);

    // Log metadata if present
    if (Object.keys(meta).length > 0) {
      const logMeta = { ...meta };
      delete logMeta.service;
      delete logMeta.operation;

      if (Object.keys(logMeta).length > 0) {
        console.table(logMeta);
      }
    }

    // Log stack trace for errors
    if (meta.error && meta.stack) {
      console.error('Stack trace:', meta.stack);
    }

    console.groupEnd();
  } else {
    // Production environment: use simple console methods
    const logData = {
      timestamp,
      level,
      message,
      ...meta,
    };

    switch (level) {
      case 'error':
        console.error(prefix + ':', message, logData);
        break;
      case 'warn':
        console.warn(prefix + ':', message, logData);
        break;
      case 'info':
        console.info(prefix + ':', message, logData);
        break;
      case 'debug':
        console.debug(prefix + ':', message, logData);
        break;
    }
  }
};

// Logger interface for type safety
interface LogMeta {
  service?: string;
  operation?: string;
  userId?: string | number;
  taskId?: string;
  orderId?: string;
  channelId?: string;
  accountId?: string | number;
  workerId?: string | number;
  error?: Error | string;
  stack?: string;
  data?: unknown;
  performance?: {
    duration?: number;
    startTime?: number;
    endTime?: number;
    currentTime?: number;
    elapsed?: number;
    mark?: string;
  };
  // HTTP/API related metadata
  method?: string;
  statusCode?: number;
  endpoint?: string;
  // Generic metadata for flexible logging
  recordCount?: number;
  [key: string]: unknown;
}

// Enhanced logging methods with structured metadata
export const Logger = {
  error: (message: string, meta: LogMeta = {}) => {
    logToConsole('error', message, meta);
  },

  warn: (message: string, meta: LogMeta = {}) => {
    logToConsole('warn', message, meta);
  },

  info: (message: string, meta: LogMeta = {}) => {
    logToConsole('info', message, meta);
  },

  debug: (message: string, meta: LogMeta = {}) => {
    logToConsole('debug', message, meta);
  },

  // Specialized methods for common use cases
  serviceError: (service: string, operation: string, error: Error | string, meta: LogMeta = {}) => {
    logToConsole('error', `${service} service error in ${operation}`, {
      ...meta,
      service,
      operation,
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    });
  },

  apiError: (endpoint: string, error: Error | string, meta: LogMeta = {}) => {
    logToConsole('error', `API error for ${endpoint}`, {
      ...meta,
      service: 'API',
      operation: endpoint,
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    });
  },

  performance: (operation: string, duration: number, meta: LogMeta = {}) => {
    logToConsole('info', `Performance: ${operation} completed`, {
      ...meta,
      operation,
      performance: {
        duration,
        ...meta.performance,
      },
    });
  },

  userAction: (action: string, meta: LogMeta = {}) => {
    logToConsole('info', `User action: ${action}`, {
      ...meta,
      operation: action,
    });
  },

  // Method to create a child logger with default metadata
  child: (defaultMeta: LogMeta) => ({
    error: (message: string, meta: LogMeta = {}) =>
      Logger.error(message, { ...defaultMeta, ...meta }),
    warn: (message: string, meta: LogMeta = {}) =>
      Logger.warn(message, { ...defaultMeta, ...meta }),
    info: (message: string, meta: LogMeta = {}) =>
      Logger.info(message, { ...defaultMeta, ...meta }),
    debug: (message: string, meta: LogMeta = {}) =>
      Logger.debug(message, { ...defaultMeta, ...meta }),
    serviceError: (service: string, operation: string, error: Error | string, meta: LogMeta = {}) =>
      Logger.serviceError(service, operation, error, { ...defaultMeta, ...meta }),
    apiError: (endpoint: string, error: Error | string, meta: LogMeta = {}) =>
      Logger.apiError(endpoint, error, { ...defaultMeta, ...meta }),
    performance: (operation: string, duration: number, meta: LogMeta = {}) =>
      Logger.performance(operation, duration, { ...defaultMeta, ...meta }),
    userAction: (action: string, meta: LogMeta = {}) =>
      Logger.userAction(action, { ...defaultMeta, ...meta }),
  }),
};

// Performance measurement utilities using browser's performance API
// Export log levels for external use
export { LOG_LEVELS };
export type { LogLevel, LogMeta };

// Default export for backward compatibility
export default Logger;
